// Source: KL-Engraving-App/extension/gcode.js.
import { parse } from "./vendor/opentype.module.js";

const CHORD_ERROR_MM = 0.02;
const MACHINE_WIDTH_MM = 160;
const MACHINE_HEIGHT_MM = 120;

export const MOTION_CHECK_PRELUDE = ["M5", "S0", "G21", "G90"];
export const MOTION_CHECK_STEPS = [
  { line: "G0 X60 Y60", settleMs: 3000 },
  { line: "G1 X100 Y60 F600", settleMs: 8000 },
  { line: "G1 X100 Y40 F6000", settleMs: 3000 },
  { line: "G0 X80 Y60", settleMs: 3000 },
];

function pointDistance(point, start, end) {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const lengthSquared = dx * dx + dy * dy;
  if (!lengthSquared) return Math.hypot(point.x - start.x, point.y - start.y);
  const projection = Math.max(
    0,
    Math.min(1, ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared)
  );
  return Math.hypot(point.x - (start.x + projection * dx), point.y - (start.y + projection * dy));
}

function midpoint(a, b) {
  return { x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
}

export function flattenQuadraticCurve(start, control, end, tolerance) {
  const points = [];
  function split(a, b, c, depth) {
    if (depth >= 20 || pointDistance(b, a, c) <= tolerance) {
      points.push(c);
      return;
    }
    const ab = midpoint(a, b);
    const bc = midpoint(b, c);
    const center = midpoint(ab, bc);
    split(a, ab, center, depth + 1);
    split(center, bc, c, depth + 1);
  }
  split(start, control, end, 0);
  return points;
}

export function flattenCubicCurve(start, control1, control2, end, tolerance) {
  const points = [];
  function split(a, b, c, d, depth) {
    if (
      depth >= 20 ||
      Math.max(pointDistance(b, a, d), pointDistance(c, a, d)) <= tolerance
    ) {
      points.push(d);
      return;
    }
    const ab = midpoint(a, b);
    const bc = midpoint(b, c);
    const cd = midpoint(c, d);
    const abc = midpoint(ab, bc);
    const bcd = midpoint(bc, cd);
    const center = midpoint(abc, bcd);
    split(a, ab, abc, center, depth + 1);
    split(center, bcd, cd, d, depth + 1);
  }
  split(start, control1, control2, end, 0);
  return points;
}

function pathContours(path, tolerance) {
  const contours = [];
  let contour = null;
  let current = null;
  for (const command of path.commands) {
    if (command.type === "M") {
      if (contour?.length) contours.push(contour);
      current = { x: command.x, y: command.y };
      contour = [current];
      continue;
    }
    if (!contour || !current) continue;
    if (command.type === "L") {
      current = { x: command.x, y: command.y };
      contour.push(current);
    } else if (command.type === "Q") {
      const end = { x: command.x, y: command.y };
      contour.push(...flattenQuadraticCurve(current, { x: command.x1, y: command.y1 }, end, tolerance));
      current = end;
    } else if (command.type === "C") {
      const end = { x: command.x, y: command.y };
      contour.push(
        ...flattenCubicCurve(
          current,
          { x: command.x1, y: command.y1 },
          { x: command.x2, y: command.y2 },
          end,
          tolerance
        )
      );
      current = end;
    } else if (command.type === "Z") {
      const start = contour[0];
      if (current.x !== start.x || current.y !== start.y) contour.push(start);
      contours.push(contour);
      contour = null;
      current = null;
    }
  }
  if (contour?.length) {
    const start = contour[0];
    if (current && (current.x !== start.x || current.y !== start.y)) contour.push(start);
    contours.push(contour);
  }
  return contours.filter((points) => points.length > 1);
}

function boundsOf(contours) {
  const points = contours.flat();
  return {
    minX: Math.min(...points.map((point) => point.x)),
    minY: Math.min(...points.map((point) => point.y)),
    maxX: Math.max(...points.map((point) => point.x)),
    maxY: Math.max(...points.map((point) => point.y)),
  };
}

export function toLdsContours(contours) {
  return contours.map((contour) => contour.map(({ x, y }) => ({ x, y: 140 - y })));
}

function transformContours(contours, bounds, scale, preset) {
  const centerX = (bounds.minX + bounds.maxX) / 2;
  const centerY = (bounds.minY + bounds.maxY) / 2;
  const radians = (preset.rotation_deg * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);
  return contours.map((contour) =>
    contour.map((point) => {
      const x = (point.x - centerX) * scale;
      const y = (point.y - centerY) * scale;
      const rotatedX = x * cos - y * sin;
      const rotatedY = x * sin + y * cos;
      return {
        x: preset.origin_x_mm + rotatedX * (preset.mirror_x ? -1 : 1),
        y: preset.origin_y_mm + rotatedY * (preset.mirror_y ? -1 : 1),
      };
    })
  );
}

function formatted(value) {
  return value.toFixed(3);
}

function rapid(point) {
  return `G0 X${formatted(point.x)} Y${formatted(point.y)}`;
}

function burn(start, end, power, feed) {
  return [
    rapid(start),
    `G1 X${formatted(end.x)} Y${formatted(end.y)} S${power} F${feed}`,
  ];
}

export function scanlineIntervals(contours, y) {
  const events = [];
  for (const contour of contours) {
    for (let index = 1; index < contour.length; index += 1) {
      const start = contour[index - 1];
      const end = contour[index];
      if (start.y === end.y) continue;
      const minY = Math.min(start.y, end.y);
      const maxY = Math.max(start.y, end.y);
      if (y < minY || y >= maxY) continue;
      const x = start.x + ((y - start.y) * (end.x - start.x)) / (end.y - start.y);
      events.push({ x, winding: end.y > start.y ? 1 : -1 });
    }
  }
  events.sort((a, b) => a.x - b.x);
  const intervals = [];
  let winding = 0;
  let startX = null;
  for (let index = 0; index < events.length;) {
    const x = events[index].x;
    let change = 0;
    while (index < events.length && Math.abs(events[index].x - x) < 1e-9) {
      change += events[index].winding;
      index += 1;
    }
    const previous = winding;
    winding += change;
    if (previous === 0 && winding !== 0) startX = x;
    if (previous !== 0 && winding === 0 && startX !== null) {
      intervals.push([startX, x]);
      startX = null;
    }
  }
  return intervals;
}

function fillLines(contours, interval, power, feed) {
  const bounds = boundsOf(contours);
  const lines = [];
  let row = 0;
  for (let y = bounds.minY + interval / 2; y <= bounds.maxY; y += interval) {
    let spans = scanlineIntervals(contours, y);
    if (row % 2 === 1) spans = spans.reverse().map(([left, right]) => [right, left]);
    for (const [startX, endX] of spans) {
      lines.push(...burn({ x: startX, y }, { x: endX, y }, power, feed));
    }
    row += 1;
  }
  return lines;
}

function outlineLines(contours, power, feed) {
  const lines = [];
  for (const contour of contours) {
    if (contour.length < 2) continue;
    lines.push(rapid(contour[0]));
    for (let index = 1; index < contour.length; index += 1) {
      lines.push(`G1 X${formatted(contour[index].x)} Y${formatted(contour[index].y)} S${power} F${feed}`);
    }
  }
  return lines;
}

function parseFont(fontBuffer) {
  const bytes = fontBuffer instanceof Uint8Array ? fontBuffer : new Uint8Array(fontBuffer);
  return parse(bytes.buffer.slice(bytes.byteOffset, bytes.byteOffset + bytes.byteLength));
}

export function findUnsupportedCharacter(fontBuffer, text) {
  const font = parseFont(fontBuffer);
  for (const character of Array.from(text)) {
    const glyph = font.charToGlyph(character);
    if (glyph.index === 0 || glyph.name === ".notdef") return character;
  }
  return null;
}

export function buildJob({ fontBuffer, text, preset, geometryOnly = false }) {
  if (typeof text !== "string" || !text.length) throw new Error("Text is required");
  const font = parseFont(fontBuffer);
  const unsupported = findUnsupportedCharacter(fontBuffer, text);
  if (unsupported) throw new Error(`This font can't engrave '${unsupported}'`);

  const path = font.getPath(text, 0, 0, font.unitsPerEm);
  const pathBounds = path.getBoundingBox();
  const width = pathBounds.x2 - pathBounds.x1;
  const capHeight = font.tables.os2?.sCapHeight || (() => {
    const capital = font.getPath("H", 0, 0, font.unitsPerEm).getBoundingBox();
    return capital.y2 - capital.y1;
  })();
  if (!capHeight || !width) throw new Error("Text has no engravable outline");
  let scale = preset.text_height_mm / capHeight;
  if (width * scale > preset.max_width_mm) scale = preset.max_width_mm / width;
  const contours = pathContours(path, CHORD_ERROR_MM / scale);
  if (!contours.length) throw new Error("Text has no engravable outline");
  const unscaledBounds = { minX: pathBounds.x1, minY: pathBounds.y1, maxX: pathBounds.x2, maxY: pathBounds.y2 };
  const transformed = transformContours(contours, unscaledBounds, scale, preset);
  const points = transformed.flat();
  const bounds = boundsOf(transformed);
  if (
    points.some(
      (point) =>
        point.x < 0 ||
        point.x > MACHINE_WIDTH_MM ||
        point.y < 0 ||
        point.y > MACHINE_HEIGHT_MM
    )
  ) {
    throw new Error("Engraving bounds exceed the LP4 regular work area");
  }

  if (geometryOnly) {
    return { bounds, contours: transformed, canvasContours: toLdsContours(transformed) };
  }
  const power = Math.round(130 + ((preset.power_pct - 1) * 125) / 99);
  const feed = Math.round(24000 - ((preset.depth_pct - 1) * 23760) / 99);
  const lines = [
    "G21",
    "G90",
    preset.laser === "ir" ? "M3011" : "M3010",
    "M4 S0",
  ];
  for (let pass = 0; pass < preset.passes; pass += 1) {
    lines.push(...fillLines(transformed, preset.line_interval_mm, power, feed));
    if (preset.outline_pass) lines.push(...outlineLines(transformed, power, feed));
  }
  lines.push("M5", rapid({ x: preset.origin_x_mm, y: preset.origin_y_mm }));

  return {
    lines,
    bounds,
    contours: transformed,
    canvasContours: toLdsContours(transformed),
    expectedFinal: { x: preset.origin_x_mm, y: preset.origin_y_mm },
  };
}

export function buildOriginTestJob({ preset }) {
  const x = preset.origin_x_mm;
  const y = preset.origin_y_mm;
  const bounds = { minX: x - 1, minY: y - 1, maxX: x + 1, maxY: y + 1 };
  if (
    ![x, y].every(Number.isFinite) ||
    bounds.minX < 0 ||
    bounds.maxX > MACHINE_WIDTH_MM ||
    bounds.minY < 0 ||
    bounds.maxY > MACHINE_HEIGHT_MM
  ) {
    throw new Error("Origin test bounds exceed the LP4 regular work area");
  }

  const power = Math.round(130 + ((preset.power_pct - 1) * 125) / 99);
  const feed = Math.round(24000 - ((preset.depth_pct - 1) * 23760) / 99);
  return {
    lines: [
      "G21",
      "G90",
      preset.laser === "ir" ? "M3011" : "M3010",
      "M4 S0",
      rapid({ x: bounds.minX, y }),
      `G1 X${formatted(bounds.maxX)} Y${formatted(y)} S${power} F${feed}`,
      rapid({ x, y: bounds.minY }),
      `G1 X${formatted(x)} Y${formatted(bounds.maxY)} S${power} F${feed}`,
      "M5",
      rapid({ x, y }),
    ],
    bounds,
    canvasContours: toLdsContours([
      [{ x: x - 1, y: y - 0.05 }, { x: x + 1, y: y - 0.05 }, { x: x + 1, y: y + 0.05 }, { x: x - 1, y: y + 0.05 }],
      [{ x: x - 0.05, y: y - 1 }, { x: x + 0.05, y: y - 1 }, { x: x + 0.05, y: y + 1 }, { x: x - 0.05, y: y + 1 }],
    ]),
    expectedFinal: { x, y },
  };
}

const scrapRectangle = (preset) => {
  const scrapBounds = { minX: -15, minY: -5, maxX: 15, maxY: 5 };
  return transformContours([[
    { x: scrapBounds.minX, y: scrapBounds.minY },
    { x: scrapBounds.maxX, y: scrapBounds.minY },
    { x: scrapBounds.maxX, y: scrapBounds.maxY },
    { x: scrapBounds.minX, y: scrapBounds.maxY },
    { x: scrapBounds.minX, y: scrapBounds.minY },
  ]], scrapBounds, 1, preset)[0];
};

export function getScrapTestPreviewBounds({ preset }) {
  const rectangleBounds = boundsOf(toLdsContours([scrapRectangle(preset)]));
  return {
    x: rectangleBounds.minX,
    y: rectangleBounds.minY,
    w: rectangleBounds.maxX - rectangleBounds.minX,
    h: rectangleBounds.maxY - rectangleBounds.minY,
  };
}

export function buildScrapTestJob({ fontBuffer, preset, geometryOnly = false }) {
  const rectangle = scrapRectangle(preset);
  if (rectangle.some((point) =>
    point.x < 0 || point.x > MACHINE_WIDTH_MM || point.y < 0 || point.y > MACHINE_HEIGHT_MM
  )) {
    throw new Error("Scrap test bounds exceed the LP4 regular work area");
  }

  const textJob = buildJob({
    fontBuffer,
    text: "TEST",
    preset: { ...preset, max_width_mm: 24 },
    geometryOnly,
  });
  if (geometryOnly) return textJob;
  const power = Math.round(130 + ((preset.power_pct - 1) * 125) / 99);
  const feed = Math.round(24000 - ((preset.depth_pct - 1) * 23760) / 99);
  const rectangleBounds = boundsOf([rectangle]);
  const bounds = {
    minX: Math.min(rectangleBounds.minX, textJob.bounds.minX),
    minY: Math.min(rectangleBounds.minY, textJob.bounds.minY),
    maxX: Math.max(rectangleBounds.maxX, textJob.bounds.maxX),
    maxY: Math.max(rectangleBounds.maxY, textJob.bounds.maxY),
  };
  const lines = [
    "G21",
    "G90",
    preset.laser === "ir" ? "M3011" : "M3010",
    "M4 S0",
    rapid(rectangle[0]),
    ...rectangle.slice(1).map((point) =>
      `G1 X${formatted(point.x)} Y${formatted(point.y)} S${power} F${feed}`
    ),
    ...textJob.lines.slice(4, -2),
    "M5",
    rapid({ x: preset.origin_x_mm, y: preset.origin_y_mm }),
  ];

  return {
    lines,
    bounds,
    contours: textJob.contours,
    canvasContours: textJob.canvasContours,
    expectedFinal: { x: preset.origin_x_mm, y: preset.origin_y_mm },
  };
}
